import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import {
  approveReview,
  escalate,
  forwardToBO,
  getReview,
  rejectReview,
  startReview,
  type ReviewRow,
} from '../api/quality.js';
import { ApiError } from '../api/client.js';

export function CompletionReviewPage(): ReactNode {
  const { id = '' } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [review, setReview] = useState<ReviewRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [reasonCode, setReasonCode] = useState('QUALITY_FAIL');

  const load = useCallback(async () => {
    try {
      setReview(await getReview(id, token));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load');
    }
  }, [id, token]);

  useEffect(() => { void load(); }, [load]);

  async function run(label: string, fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `${label}: ${err.message}` : `${label} failed`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="completion-review">
      <h1>Review {id}</h1>
      {error ? <div role="alert">{error}</div> : null}
      {review ? (
        <section aria-label="review-summary">
          <div>Status: <strong>{review.status}</strong></div>
          <div>Vendor: {review.vendorId ?? '—'}</div>
          <div>Painter: {review.painterUserId ?? '—'}</div>
          <div>Reviewer: {review.assignedReviewerUserId ?? '—'}</div>
        </section>
      ) : <div role="status">Loading…</div>}

      <nav aria-label="review-nav">
        <Link to={`/vendor/reviews/${id}/photos`}>Photos</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/gps`}>GPS</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/areas`}>Areas</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/materials`}>Materials</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/rework`}>Rework</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/evidence`}>Evidence</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/score`}>Score</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/performance`}>Performance</Link>{' | '}
        <Link to={`/vendor/reviews/${id}/audit`}>Audit</Link>
      </nav>

      <section aria-label="actions">
        <button type="button" disabled={busy} onClick={() => void run('Start', () => startReview(id, token))}>Start review</button>
        <label>Reason
          <select aria-label="reason-code" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
            <option value="QUALITY_FAIL">QUALITY_FAIL</option>
            <option value="GPS_MISMATCH">GPS_MISMATCH</option>
            <option value="AREA_VARIANCE">AREA_VARIANCE</option>
            <option value="MATERIAL_VARIANCE">MATERIAL_VARIANCE</option>
            <option value="PHOTO_INVALID">PHOTO_INVALID</option>
          </select>
        </label>
        <label>Remarks
          <textarea aria-label="action-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </label>
        <button type="button" disabled={busy || !remarks} onClick={() => void run('Approve', () => approveReview(id, remarks, token))}>Approve</button>
        <button type="button" disabled={busy || !remarks} onClick={() => void run('Reject', () => rejectReview(id, reasonCode, remarks, token))}>Reject</button>
        <button type="button" disabled={busy || !remarks} onClick={() => void run('Forward', () => forwardToBO(id, remarks, token))}>Forward to BO</button>
        <button type="button" disabled={busy || !remarks} onClick={() => void run('Escalate', () => escalate(id, remarks, token))}>Escalate</button>
      </section>
    </main>
  );
}
